import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Printer, X, MapPin, Phone } from "lucide-react";
import { api, fileUrl, formatError } from "@/lib/api";
import { Page } from "@/components/Page";
import { formSpecs } from "@/lib/formSpecs";
import { GISearchSelect } from "@/components/GISearchSelect";
import { DocumentsUploader, areMandatoryDocsFilled } from "@/components/DocumentsUploader";
import { numberToIndianWords } from "@/lib/numberToWords";

const GI_STORAGE_KEY = "tv_selected_gi";
const SOC_STORAGE_KEY = "tv_soc_data"; // shared subset for NOC auto-fill
const SIGNATURE_STORAGE_KEY = "tv_signature_doc"; // shared signature image across GI-3A & Statement of Case

function fill(str, values) {
  return String(str || "").replace(/\{(\w+)\}/g, (_, k) => {
    const v = values[k];
    if (v === undefined || v === null || String(v).trim() === "") return "________________________";
    return String(v);
  });
}

export default function FormBuilder() {
  const { formType } = useParams();
  const spec = formSpecs[formType] || formSpecs["GI-3A"];
  const [values, setValues] = useState({});
  const [signature, setSignature] = useState({ date: "", place: "" });
  const [documents, setDocuments] = useState({});
  const [signatureImage, setSignatureImage] = useState(""); // shared signature image path
  const [giOptions, setGIOptions] = useState([]);
  const [giApps, setGIApps] = useState({}); // name -> application number
  const [busy, setBusy] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [error, setError] = useState("");

  // Load GI catalog once
  useEffect(() => {
    api.get("/gi-items").then(r => {
      setGIOptions(r.data?.items || []);
      setGIApps(r.data?.applications || {});
    }).catch(() => {});
  }, []);

  // Bootstrap cross-form auto-fill
  useEffect(() => {
    let stored = "";
    let soc = {};
    let sig = "";
    try {
      stored = localStorage.getItem(GI_STORAGE_KEY) || "";
      soc = JSON.parse(localStorage.getItem(SOC_STORAGE_KEY) || "{}");
      sig = localStorage.getItem(SIGNATURE_STORAGE_KEY) || "";
    } catch {}

    // Preload signature into current form
    if (sig) {
      setSignatureImage(sig);
      if (formType === "GI-3A") {
        // Seed documents.signature so the uploader shows it as already filled
        setDocuments(prev => prev.signature ? prev : { ...prev, signature: { path: sig, kind: "image", name: "signature" } });
      }
    }

    setValues(prev => {
      const next = { ...prev };
      if (stored) {
        if (formType === "GI-3A") next.registered_gi = next.registered_gi || stored;
        else if (formType === "Statement-of-Case") next.gi_name = next.gi_name || stored;
        else if (formType === "NOC") {
          next.producer_item = next.producer_item || stored;
          // Auto-fill application number from catalog if not already set
        }
      }
      if (formType === "NOC") {
        if (soc.artisan_name) next.producer_name = next.producer_name || soc.artisan_name;
        if (soc.gi_name && !next.producer_item) next.producer_item = soc.gi_name;
      }
      return next;
    });
  }, [formType]);

  // When GI catalog loads OR selected GI changes, refresh NOC's gi_application from the catalog
  useEffect(() => {
    if (formType !== "NOC") return;
    setValues(prev => {
      const chosen = prev.producer_item;
      if (!chosen) return prev;
      const app = giApps[chosen];
      if (app && prev.gi_application !== app) {
        return { ...prev, gi_application: app };
      }
      return prev;
    });
  }, [formType, giApps, values.producer_item]);

  const set = (name, value) => setValues(v => {
    const next = { ...v, [name]: value };
    // Statement of Case: auto-derive annual_words from annual_amount
    if (formType === "Statement-of-Case" && name === "annual_amount") {
      const words = numberToIndianWords(value).replace(/ Rupees$/i, "").trim();
      next.annual_words = words;
    }
    // Statement of Case: persist a subset for NOC to consume
    if (formType === "Statement-of-Case") {
      try {
        const soc = { artisan_name: next.artisan_name, gi_name: next.gi_name };
        localStorage.setItem(SOC_STORAGE_KEY, JSON.stringify(soc));
      } catch {}
    }
    return next;
  });

  const setGI = (val) => {
    const key = formType === "GI-3A" ? "registered_gi" : formType === "Statement-of-Case" ? "gi_name" : "producer_item";
    setValues(v => {
      const next = { ...v, [key]: val };
      if (formType === "NOC") {
        // Also refresh application number when user picks a GI directly on NOC
        const app = giApps[val];
        if (app) next.gi_application = app;
      }
      if (formType === "Statement-of-Case") {
        try {
          const soc = { artisan_name: next.artisan_name, gi_name: val };
          localStorage.setItem(SOC_STORAGE_KEY, JSON.stringify(soc));
        } catch {}
      }
      return next;
    });
    try { localStorage.setItem(GI_STORAGE_KEY, val || ""); } catch {}
  };

  const onDocumentsChange = (next) => {
    setDocuments(next);
    const sigPath = next?.signature?.path || "";
    setSignatureImage(sigPath);
    try {
      if (sigPath) localStorage.setItem(SIGNATURE_STORAGE_KEY, sigPath);
      else localStorage.removeItem(SIGNATURE_STORAGE_KEY);
    } catch {}
  };

  const allValues = useMemo(() => ({ ...values, signature_date: signature.date, signature_place: signature.place }), [values, signature]);

  const submit = async () => {
    setError("");
    if (formType === "GI-3A" && !documents?.photo?.path) {
      const m = "Please upload the mandatory Photo before saving the GI 3A form.";
      setError(m); toast.error(m); return;
    }
    if (formType === "GI-3A" && !documents?.aadhaar?.path) {
      const m = "Please upload the mandatory Aadhaar Card before saving.";
      setError(m); toast.error(m); return;
    }
    setBusy(true);
    try {
      const payload = {
        ...values,
        signature_date: signature.date,
        signature_place: signature.place,
        ...(formType === "GI-3A" ? { documents } : {}),
      };
      await api.post(`/submissions/${formType}`, payload);
      setPopupOpen(true);
    } catch (err) {
      const m = formatError(err);
      setError(m); toast.error(m);
    } finally {
      setBusy(false);
    }
  };

  const renderField = (f) => {
    // Search-select for the GI fields (also used on NOC now for producer_item)
    if ((formType === "GI-3A" && f.name === "registered_gi") ||
        (formType === "Statement-of-Case" && f.name === "gi_name") ||
        (formType === "NOC" && f.name === "producer_item")) {
      return (
        <label key={f.name}>
          {f.label}
          <GISearchSelect
            value={values[f.name] || ""}
            onChange={setGI}
            options={giOptions}
            placeholder={giOptions.length ? "Type to search from IP-India GI list…" : "Loading GI catalog…"}
            testId={`form-${f.name}-select`}
          />
          <span className="hint-small">Selection is remembered across GI 3A, Statement of Case and NOC forms.</span>
        </label>
      );
    }
    // Read-only auto-filled fields
    if (formType === "Statement-of-Case" && f.name === "annual_words") {
      return (
        <label key={f.name}>
          {f.label}
          <input
            data-testid={`form-${f.name}-input`}
            value={values[f.name] || ""}
            readOnly
            placeholder="Auto-filled from annual earnings"
            style={{ background: "#F8FAF3", cursor: "not-allowed" }}
          />
          <span className="hint-small">Auto-calculated from annual earnings above.</span>
        </label>
      );
    }
    return (
      <label key={f.name}>
        {f.label}
        {f.type === "textarea" ? (
          <textarea data-testid={`form-${f.name}-input`} value={values[f.name] || ""} onChange={e => set(f.name, e.target.value)} placeholder={f.placeholder || ""} />
        ) : (
          <input data-testid={`form-${f.name}-input`} type={f.type || "text"} value={values[f.name] || ""} onChange={e => set(f.name, e.target.value)} placeholder={f.placeholder || ""} />
        )}
      </label>
    );
  };

  const mandatoryOk = formType !== "GI-3A" || areMandatoryDocsFilled(documents);
  const letterClass = `letter-sheet${spec.letterhead ? " letterhead" : ""}`;

  return (
    <Page title={spec.title} subtitle={spec.subtitle}>
      {formType === "GI-3A" && (
        <div className="form-instructions no-print" data-testid="gi3a-instructions">
          <div className="form-instructions-badge">Please read carefully before filling the form</div>
          <ul>
            <li><b>Who fills this form:</b> If the applicant is an individual, enter the <b>Artisan's Name</b>. If the applicant is a firm / enterprise / company, enter the <b>Firm / Enterprise / Company Name</b>.</li>
            <li><b>Required documents (individual artisan):</b> colour photograph with a white background, Aadhaar Card, and Artisan / Craftsman Card <em>or</em> a training / competency certificate related to the specific GI item.</li>
            <li><b>Required documents (firm / enterprise / company):</b> Firm / Enterprise / Company Registration Certificate, GST Certificate, colour photograph of the Proprietor / Director with a white background, Aadhaar Card of the Proprietor / Director, and a cancelled cheque or bill copy bearing the Firm Name and Registration Number.</li>
            <li><b>For more information, contact True Vision Welfare Society:</b> <a href="tel:+917017830919">7017830919</a>, <a href="tel:+919411216027">9411216027</a>.</li>
          </ul>
        </div>
      )}
      {formType === "NOC" && (
        <div className="noc-hint no-print" data-testid="noc-hint">
          <b>Auto-filled:</b> Producer name comes from your Statement of Case, GI item from the Statement of Case selection, and the G.I. Application Number is looked up from the IP-India catalog. Enter only the <b>Class</b> below. Print on the Registered Proprietor's letterhead — the letter leaves a 3-inch top margin for it.
        </div>
      )}
      <div className="print-toolbar no-print">
        <span>Fill the fields below, then print or save as PDF. Only the letter preview will appear on the printed page.</span>
        <div>
          <button className="secondary-btn" onClick={() => window.print()} data-testid="print-form-button"><Printer size={14} /> Print / Save PDF</button>
          <button className="primary-btn" onClick={submit} disabled={busy || (formType === "GI-3A" && !mandatoryOk)} data-testid="submit-filled-form-button">
            {busy ? "Saving…" : "Save filled form"} <ArrowUpRight size={16} />
          </button>
        </div>
      </div>

      {/* Fill-in inputs — hidden from print */}
      <section className="fill-panel no-print" data-testid={`filler-${formType}`}>
        <div className="legal-fields">
          {spec.fields.map(renderField)}
        </div>
        <div className="signature-row">
          <label>Date
            <input data-testid="form-signature-date-input" type="date" value={signature.date} onChange={e => setSignature({ ...signature, date: e.target.value })} />
          </label>
          <label>Place
            <input data-testid="form-signature-place-input" value={signature.place} onChange={e => setSignature({ ...signature, place: e.target.value })} />
          </label>
        </div>
        {error && <div className="error" data-testid="filled-form-error">{error}</div>}
      </section>

      {/* Documents uploader — only on GI-3A submission */}
      {formType === "GI-3A" && (
        <div className="no-print">
          <DocumentsUploader value={documents} onChange={onDocumentsChange} />
        </div>
      )}

      {/* Letter preview — this is what prints */}
      <article className={letterClass} data-testid={`letter-${formType}`}>
        {spec.letterhead && <div className="letter-letterhead-space" aria-hidden="true" data-testid="letterhead-space"><span>Reserved for Registered Proprietor letterhead</span></div>}
        {spec.letterHeadLines && spec.letterHeadLines.length > 0 && (
          <div className="letter-head">
            {spec.letterHeadLines.map((h) => <p key={h}>{h}</p>)}
          </div>
        )}

        <h1 className="letter-title" data-testid="letter-title">{spec.letterTitle}</h1>
        {spec.letterSubtitle && <p className="letter-subtitle">{spec.letterSubtitle}</p>}
        {spec.letterMeta && spec.letterMeta.length > 0 && (
          <div className="letter-meta">
            {spec.letterMeta.map((m) => <span key={m}>{m}</span>)}
          </div>
        )}

        <div className="letter-body" data-testid="letter-body">
          {(spec.letterBody || []).map((line, i) => (
            <p key={i} className={line === "" ? "spacer" : ""}>{line === "" ? "\u00A0" : fill(line, allValues)}</p>
          ))}
        </div>

        {spec.letterFooter && (
          <div className="letter-footer">
            <div className="letter-footer-left">
              {(spec.letterFooter.leftPairs || []).map(([lbl, val], i) => (
                <p key={i}><b>{lbl}</b> {fill(val, allValues)}</p>
              ))}
            </div>
            <div className="letter-footer-right">
              {signatureImage && (formType === "GI-3A" || formType === "Statement-of-Case") && (
                <img
                  src={fileUrl(signatureImage)}
                  alt="Signature"
                  className="letter-signature-image"
                  data-testid="letter-signature-image"
                  style={{ maxHeight: 60, maxWidth: 180, objectFit: "contain", display: "block", marginBottom: 2 }}
                />
              )}
              {String(spec.letterFooter.rightLabel || "SIGNATURE").split("\n").map((r, i) => <p key={i}>{r}</p>)}
            </div>
          </div>
        )}

        {spec.letterNote && <p className="letter-note">{spec.letterNote}</p>}
      </article>

      {popupOpen && (
        <div className="submit-modal-overlay no-print" role="dialog" aria-modal="true" data-testid="submit-success-modal">
          <div className="submit-modal">
            <button className="submit-modal-close" onClick={() => setPopupOpen(false)} aria-label="Close" data-testid="submit-modal-close">
              <X size={16} />
            </button>
            <div className="submit-modal-badge">Filled form saved</div>
            <h2>Send the signed hard copy</h2>
            <p>Please send the <b>signed hard copy of the GI 3A form</b> along with the <b>Statement of Case, NOC, photo and supporting documents</b> to the correspondence office of True Vision Welfare Society:</p>
            <address className="submit-modal-address">
              <MapPin size={14} />
              <div>
                <b>True Vision Welfare Society</b><br/>
                H.No. 58, Near Water Tank,<br/>
                Village Nagla Sharkey, Budaun (U.P.) – 243601<br/>
                <Phone size={12} style={{ verticalAlign: "-1px" }} /> <a href="tel:+918077967066">+91 8077967066</a>
              </div>
            </address>
            <p className="submit-modal-tip">Keep a photocopy of everything you post for your records. We will update your submission status here once the originals are received.</p>
            <button className="primary-btn" onClick={() => setPopupOpen(false)} data-testid="submit-modal-ok">Got it</button>
          </div>
        </div>
      )}
    </Page>
  );
}

